import React from 'react';
import { CheckCircle2 } from 'lucide-react';

interface ScanProgressBarProps {
  processed: number;
  total: number;
  available: number;
  isDone: boolean;
}

const ScanProgressBar: React.FC<ScanProgressBarProps> = ({ processed, total, available, isDone }) => {
  if (total === 0 && !isDone) return null;

  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 100;

  const doneMessage = () => {
    if (available === 0) return 'No domains available — try a different keyword';
    if (available === 1) return '1 domain available — scroll down to see it';
    if (available <= 3) return `${available} domains available — scroll down to see them`;
    return `${available} domains available — consider registering before they're gone`;
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-2.5">
      <div className="max-w-7xl mx-auto">
        {isDone ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 className={`h-4 w-4 flex-shrink-0 ${available > 0 ? 'text-green-500' : 'text-gray-400'}`} />
            <span className="text-sm text-gray-700">
              <span className="font-semibold">Scanned {total} TLDs</span>
              <span className="text-gray-400 mx-1.5">·</span>
              <span className={available > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                {doneMessage()}
              </span>
            </span>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Scanning{' '}
                <span className="font-semibold text-gray-700 tabular-nums">{processed}</span>
                {' / '}
                <span className="font-semibold text-gray-700 tabular-nums">{total}</span>
                {' TLDs'}
                {available > 0 && (
                  <span className="text-green-600 font-semibold ml-1.5">
                    · {available} available
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-400 tabular-nums">{percent}%</span>
            </div>
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanProgressBar;
